import { PublicProduct } from "@/hooks/usePublicMenu";
import { PublicBanner } from "@/hooks/useRestaurantBanners";
import { TabletProductCard } from "./TabletProductCard";
import { Star, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface TabletHighlightsSectionProps {
  banners: PublicBanner[];
  products: PublicProduct[];
  getItemQuantity: (productId: string) => number;
  onAddProduct: (product: PublicProduct, notes?: string) => void;
  onRemoveProduct: (productId: string) => void;
  onNavigateToMenu: () => void;
  onBannerClick?: (banner: PublicBanner) => void;
}

export function TabletHighlightsSection({
  banners,
  products,
  getItemQuantity,
  onAddProduct,
  onRemoveProduct,
  onNavigateToMenu,
  onBannerClick,
}: TabletHighlightsSectionProps) {
  // Get featured products first, then fallback to first 6 products
  const featuredProducts = products.filter(p => (p as any).is_featured);
  const highlightedProducts = featuredProducts.length > 0 
    ? featuredProducts.slice(0, 6)
    : products.slice(0, 6);
  const topBanner = banners[0];

  return (
    <div className="flex-1 overflow-auto p-6 space-y-8">
      {/* Hero banner */}
      {topBanner && (
        <div
          className="relative h-64 rounded-2xl overflow-hidden cursor-pointer group"
          onClick={() => onBannerClick?.(topBanner)}
        >
          <img
            src={topBanner.image_url}
            alt={topBanner.title || "Promoção"}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            {topBanner.title && (
              <h2 className="text-3xl font-bold text-white mb-2">
                {topBanner.title}
              </h2>
            )}
            {topBanner.subtitle && (
              <p className="text-lg text-white/80">{topBanner.subtitle}</p>
            )}
          </div>
        </div>
      )}

      {/* Highlighted products */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 text-primary fill-primary" />
            <h2 className="text-2xl font-bold text-white">Destaques</h2>
          </div>
          <Button
            variant="ghost"
            className="text-primary hover:text-primary/80"
            onClick={onNavigateToMenu}
          >
            Ver cardápio completo
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {highlightedProducts.map((product) => (
            <TabletProductCard
              key={product.id}
              product={product}
              quantity={getItemQuantity(product.id)}
              onAdd={(notes) => onAddProduct(product, notes)}
              onRemove={() => onRemoveProduct(product.id)}
            />
          ))}
        </div>
      </div>

      {/* Additional banners */}
      {banners.length > 1 && (
        <div className="grid grid-cols-2 gap-4">
          {banners.slice(1, 3).map((banner) => (
            <div
              key={banner.id}
              className="relative h-40 rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => onBannerClick?.(banner)}
            >
              <img
                src={banner.image_url}
                alt={banner.title || "Promoção"}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              {banner.title && (
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-lg font-bold text-white truncate">
                    {banner.title}
                  </h3>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
